import React from 'react';
import * as Api from '../../lib/Api/';

import { Me, Category, Country, Language, PodcastMetadata } from '../../types';
import { AnimatedReleasePreview, PREVIEW_CELEBRATION } from '../AddPodcast/PreviewPane';
import {
  DialogFullScreen,
  ButtonPrimary,
  ButtonTertiary,
  NavStepper,
  NavStepperItem,
} from '@spotify-internal/encore-creator-web';
import { Podcast } from '../../types';
import styled from 'styled-components';
import { Review } from './Review';

import { ClaimPodcastContainer } from './ClaimPodcastContainer';
import { AddMetadataContainer } from './AddMetadataContainer';
import './index.module.scss';
import { RouteComponentProps } from 'react-router';

const Container = styled.div`
  max-width: 70%;
  margin-left: auto;
  margin-right: auto;
`;

const Footer = styled.div`
  height: 48px;
  justify-content: flex-end;
`;

enum Steps {
  Claim,
  AddMetadata,
  Review,
  Success,
}

interface Props extends Pick<RouteComponentProps, 'history' | 'location'> {
  me: Me;
  loadMe(): void;
  podcastMetadata: Podcast;
  onClaimError(): void;
}

export function SoundtrapPodcastModal(props: Props) {
  const [currentStep, setCurrentStep] = React.useState(Steps.Claim);
  const [submitDisabled, setSubmitDisabled] = React.useState(true);
  const [metadata, setMetadata] = React.useState<null | PodcastMetadata>(null);
  const [countries, setCountries] = React.useState<null | Country[]>(null);
  const [languages, setLanguages] = React.useState<null | Language[]>(null);
  const [categories, setCategories] = React.useState<null | Category[]>(null);

  React.useEffect(() => {
    let didCancel = false;

    async function getData() {
      const [countryData, languageData, categoryData] = await Promise.all([
        Api.getCountries(),
        Api.getLanguages(),
        Api.getCategories(),
      ]);

      if (didCancel) {
        return;
      }

      setCountries(countryData);
      setLanguages(languageData);
      setCategories(categoryData);
    }

    getData();

    return () => {
      didCancel = true;
    };
  }, []);

  function renderBody() {
    switch (currentStep) {
      case Steps.Claim:
        return (
          <ClaimPodcastContainer
            redactedEmailAddress={props.podcastMetadata.shows[0].redactedEmailAddress}
            showId={props.podcastMetadata.shows[0].id}
            onSuccessfulValidation={() => setSubmitDisabled(false)}
          />
        );
      case Steps.AddMetadata:
        /**
         * Data is still loading…
         */
        if (!categories || !languages || !countries) {
          return null;
        }

        return (
          <AddMetadataContainer
            categories={categories}
            countries={countries}
            languages={languages}
            metadata={metadata}
            onSuccessfulValidation={metadata => {
              setMetadata(metadata);
              setSubmitDisabled(false);
            }}
          />
        );
      case Steps.Review:
        return (
          <Review
            countryOfOrigin={metadata!.countryOfOrigin}
            language={metadata!.language}
            categories={metadata!.categories!}
          />
        );
      case Steps.Success:
        return (
          <AnimatedReleasePreview
            previewMode={PREVIEW_CELEBRATION}
            existingPodcast={!!props.podcastMetadata.isExistingShow}
            rssFeed={props.podcastMetadata}
            loadMe={props.loadMe}
            unsetSubmitFeedStatus={/* istanbul ignore next */ () => {}}
            history={props.history}
            location={props.location}
            shouldFadeIn={false}
          />
        );
    }
  }

  function renderFooter() {
    function handleClick() {
      function advanceStep() {
        setCurrentStep(step => step + 1);
        setSubmitDisabled(true);
      }

      const actions: {
        [step: string]: () => void;
      } = {
        [Steps.Claim]: () => advanceStep(),
        [Steps.AddMetadata]: () => {
          setCurrentStep(step => step + 1);
          setSubmitDisabled(false);
        },
        [Steps.Review]: () => {
          advanceStep();
          Api.updateMetadata(props.podcastMetadata.shows[0].id, metadata!);
        },
      };

      const action = actions[currentStep];

      if (!action) {
        throw `No action for currentStep: ${currentStep}. ${JSON.stringify(
          String(actions[currentStep]),
          null,
          2,
        )}`;
      }
      action();
    }

    function handlePreviousClick() {
      /**
       * Going back from `Steps.Review` -> `Steps.AddMetadata` should not
       * disable the submit button.
       */
      setSubmitDisabled(false);
      setCurrentStep(Steps.AddMetadata);
    }

    return (
      <Footer>
        {currentStep === Steps.Review && (
          <ButtonTertiary
            data-testid="previous-button"
            onClick={handlePreviousClick}
            style={{
              marginRight: 'auto',
            }}
          >
            Go Back
          </ButtonTertiary>
        )}
        <ButtonPrimary
          key={currentStep}
          data-testid="continue-button"
          disabled={submitDisabled}
          onClick={handleClick}
        >
          {currentStep === Steps.Review ? 'Submit' : 'Next'}
        </ButtonPrimary>
      </Footer>
    );
  }

  return (
    <DialogFullScreen
      data-testid="soundtrap-submit-modal"
      dialogTitle="Spotify for Podcasters"
      navStepper={
        <NavStepper>
          <NavStepperItem visited={currentStep > Steps.Claim} active={currentStep === Steps.Claim}>
            Verify
          </NavStepperItem>
          <NavStepperItem
            visited={currentStep > Steps.AddMetadata}
            active={currentStep === Steps.AddMetadata}
          >
            Add podcast info
          </NavStepperItem>
          <NavStepperItem
            visited={currentStep > Steps.Review}
            active={currentStep === Steps.Review}
          >
            Review and submit
          </NavStepperItem>
        </NavStepper>
      }
      body={<Container>{renderBody()}</Container>}
      footer={renderFooter()}
    />
  );
}
